// Settings → Claude access: personal API tokens that let Claude (and scripts) use HHL Ops as you.
import { Check, Copy, KeyRound } from "lucide-react";
import { useState } from "react";
import { ago } from "../lib/format.ts";
import { mutate, mutateWithResult } from "../lib/mutate.ts";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, Input } from "../ui/controls.tsx";

const MCP_URL = `${location.origin}/mcp`;

function CopyBox({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-start gap-2 rounded-md border border-line bg-surface-2 p-2">
      <code className="min-w-0 flex-1 font-mono text-[12px] break-all whitespace-pre-wrap">{text}</code>
      <Button
        size="sm"
        icon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        onClick={async () => {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : label}
      </Button>
    </div>
  );
}

export function McpPage() {
  const user = me();
  const tokens = useRows("api_token");
  const users = useRows("app_user");
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string | null>(null);
  const visible = tokens.filter((t) => user?.role === "admin" || t.user_id === user?.id).sort((a, b) => b.created_at.localeCompare(a.created_at));

  const create = async () => {
    const { ok, result } = await mutateWithResult("token.create", { id: crypto.randomUUID(), name: name || "Claude Code" });
    if (ok) {
      setCreated((result as { token: string }).token);
      setName("");
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-8 py-8">
        <h1 className="text-xl font-semibold">Claude access</h1>
        <p className="mt-1 text-muted">
          Let Claude look things up in HHL Ops (items, vendors, prices, MOQs, lead times, projects, stock). A token acts as you, with your role. Treat it like a
          password.
        </p>

        <div className="mt-6 rounded-lg border border-line bg-surface p-4">
          <div className="font-medium">New token</div>
          <div className="mt-2 flex gap-2">
            <Input
              value={name}
              placeholder="What it's for, e.g. Claude Code on my PC"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && create()}
            />
            <Button variant="primary" icon={<KeyRound className="size-4" />} onClick={create}>
              Create token
            </Button>
          </div>
          {created && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="text-[12px] text-warn">Copy it now: HHL Ops only keeps a fingerprint, so it can't show this token again.</div>
              <CopyBox text={created} label="Copy token" />
              <div className="mt-2 text-[12px] text-muted">Add HHL Ops to Claude Code (run once in a terminal):</div>
              <CopyBox
                text={`claude mcp add --transport http --scope user hhl-ops ${MCP_URL} --header "Authorization: Bearer ${created}"`}
                label="Copy command"
              />
            </div>
          )}
        </div>

        <h2 className="mt-8 mb-2 font-semibold">Tokens</h2>
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {!visible.length && <div className="p-4 text-muted">No tokens yet.</div>}
          {visible.map((t) => (
            <div key={t.id} className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0">
              <KeyRound className="size-4 text-faint" />
              <div className="min-w-0 flex-1">
                <div className={t.revoked_at ? "text-faint line-through" : "font-medium"}>{t.name}</div>
                <div className="text-[12px] text-faint">
                  {user?.role === "admin" && `${users.find((u) => u.id === t.user_id)?.name || "Someone"} · `}
                  made {ago(t.created_at)} · {t.last_used_at ? `last used ${ago(t.last_used_at)}` : "never used"}
                </div>
              </div>
              {t.revoked_at ? (
                <Badge>Revoked</Badge>
              ) : (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => confirm(`Revoke "${t.name}"? Anything using it stops working.`) && mutate("token.revoke", { id: t.id })}
                >
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </div>
        <p className="mt-4 text-[12px] text-faint">
          Server: {MCP_URL} (Streamable HTTP, Bearer token). edit_variants and edit_products change Shopify items as you; everything else is read-only.
        </p>
      </div>
    </div>
  );
}
